/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { InjectionToken } from '@angular/core';
import { ItemWithUploadUrls } from '@app/app/shared/models/ItemWithUploadUrl';
import { Page } from '@app/app/shared/models/page';
import { Observable } from 'rxjs';
import {
  AssessmentSummary,
  AssessmentTextAnalysisStatus,
  CreateReportConditionalSectionRequest,
  CreateReportFeedbackSectionRequest,
  CreateReportResourceSectionRequest,
  CreateReportStandardChartSectionRequest,
  CreateReportStandardSectionRequest,
  DiagnosticDescriptionQuestionResponse,
  DiagnosticLogic,
  DiagnosticQuestionRequest,
  DiagnosticQuestionResponse,
  Diagnostics,
  DiagnosticsRetake,
  FeedbackProviderSettingRequest,
  FeedbackProviderSettings,
  FeedbackProviderUser,
  FeedbackRecipientSummaryEntry,
  GeneratedReport,
  LearningGoalIconRequest,
  LogicGoal,
  LogicGoalLevel,
  MyPagesAssessmentsResponse,
  QuestionEditor,
  QuestionResponseRequest,
  QuestionResponses,
  Report,
  ReportMode,
  ReportReordering,
  SelectedFeedbackProvidersRequest,
  ShowReportLogoRequest,
  UpdateReportConditionalSectionRequest,
  UpdateReportFeedbackProvidersRequest,
  UpdateReportFeedbackQuestionRequest,
  UpdateReportFeedbackStatsRequest,
  UpdateReportResourceSectionRequest,
  UpdateReportSectionResponse,
  UpdateReportStandardSectionRequest,
  UpdateReportTitleRequest,
} from '../../../shared/models';
import { ObservableResult } from '../../../shared/store';

/** Injection token for the data service, used by angular to decide whether to inject Mock or API service at runtime. */
export const DIAGNOSTICS_DATA_SERVICE = new InjectionToken<DiagnosticsDataService>('[Diagnostics] DataService');

/** The discovery data service. */
export interface DiagnosticsDataService {
  createQuestion(
    assessmentUid: string,
    questionData: DiagnosticQuestionRequest,
    languageCode?: string,
  ): ObservableResult<DiagnosticQuestionResponse>;

  updateQuestion(
    assessmentUid: string,
    questionUid: string,
    questionData: DiagnosticQuestionRequest,
    languageCode?: string,
  ): ObservableResult<DiagnosticQuestionResponse>;

  updateQuestionDescription(
    assessmentUid: string,
    questionUid: string,
    questionData: QuestionEditor,
    languageCode?: string,
  ): ObservableResult<DiagnosticDescriptionQuestionResponse>;

  copyQuestion(assessmentUid: string, questionUid: string): ObservableResult<DiagnosticQuestionResponse>;

  deleteQuestion(assessmentUid: string, questionUid: string): ObservableResult<void>;

  reorderQuestions(assessmentUid: string, questions: string[]): ObservableResult<DiagnosticQuestionResponse[]>;

  getDiagnosticsLogicGoals(assessmentUid: string): ObservableResult<DiagnosticLogic[]>;

  updateLogicGoal(assessmentUid: string, goalUid: string, goalData: LogicGoal): ObservableResult<Diagnostics>;

  updateLogicGoalLevel(assessmentUid: string, goalUid: string, levelUid: string, levelData: LogicGoalLevel): ObservableResult<Diagnostics>;

  updateAssessmentChartIcon(
    assessmentUid: string,
    sectionUid: string,
    dynamicContentUid: string,
    type: 'MAX' | 'MIN',
    iconData: LearningGoalIconRequest,
    file: File,
  ): ObservableResult<void>;

  updateLearningGoalIcon(
    assessmentUid: string,
    goalUid: string,
    type: 'MAIN' | 'MAX' | 'MIN',
    iconData: LearningGoalIconRequest,
    file: File,
  ): ObservableResult<void>;

  removeLearningGoalIcon(assessmentUid: string, goalUid: string, type: 'MAIN' | 'MAX' | 'MIN'): ObservableResult<void>;

  removeAssessmentChartIcon(
    assessmentUid: string,
    sectionUid: string,
    dynamicContentUid: string,
    type: 'MAX' | 'MIN',
  ): ObservableResult<void>;

  getQuestionResponses(assessmentUid: string, feedbackFor?: string): ObservableResult<QuestionResponses>;

  addQuestionResponse(
    assessmentUid: string,
    version: number,
    request: QuestionResponseRequest,
  ): ObservableResult<ItemWithUploadUrls<QuestionResponses>>;

  updateDiagnosticsRetake(assessmentUid: string, request: DiagnosticsRetake): ObservableResult<QuestionResponses>;

  randomizeQuestions(assessmentUid: string): ObservableResult<QuestionResponses>;

  deleteRandomizeQuestions(assessmentUid: string): ObservableResult<QuestionResponses>;

  getAssessmentSummary(
    assessmentUid: string,
    languageCode?: string,
    feedbackFor?: string,
    targetUserUid?: string,
  ): ObservableResult<AssessmentSummary>;

  getAssessmentSummaryAdmins(
    assessmentUid: string,
    userUid: string,
    languageCode?: string,
    feedbackFor?: string,
  ): ObservableResult<AssessmentSummary>;

  getDiagnosticsQuestions(assessmentUid: string, languageCode?: string): ObservableResult<DiagnosticQuestionResponse[]>;

  addSelectedFeedbackProviders(
    assessmentUid: string,
    questionResponseUid: string,
    request: SelectedFeedbackProvidersRequest,
  ): ObservableResult<void>;

  updateFeedbackProvidersSettings(
    assessmentUid: string,
    request: FeedbackProviderSettingRequest,
  ): ObservableResult<FeedbackProviderSettings>;

  searchFeedbackProviders(
    assessmentUid: string,
    questionResponseUid: string,
    term: string,
    page: number,
    size: number,
  ): ObservableResult<Page<FeedbackProviderUser>>;

  /** Reports. */

  createReportStandardSection(
    assessmentUid: string,
    request: CreateReportStandardSectionRequest,
    languageCode?: string,
  ): ObservableResult<UpdateReportSectionResponse>;

  createReportStandardChartSection(
    assessmentUid: string,
    request: CreateReportStandardChartSectionRequest,
    languageCode?: string,
  ): ObservableResult<UpdateReportSectionResponse>;

  createReportConditionalSection(
    assessmentUid: string,
    request: CreateReportConditionalSectionRequest,
    languageCode?: string,
  ): ObservableResult<UpdateReportSectionResponse>;

  createReportResourceSection(
    assessmentUid: string,
    request: CreateReportResourceSectionRequest,
    languageCode?: string,
  ): ObservableResult<UpdateReportSectionResponse>;

  createReportFeedbackSection(
    assessmentUid: string,
    request: CreateReportFeedbackSectionRequest,
    languageCode?: string,
  ): ObservableResult<UpdateReportSectionResponse>;

  updateReportTitle(assessmentUid: string, request: UpdateReportTitleRequest, languageCode?: string): ObservableResult<void>;

  updateReportStandardSection(
    assessmentUid: string,
    sectionUid: string,
    request: UpdateReportStandardSectionRequest,
    languageCode?: string,
  ): ObservableResult<UpdateReportSectionResponse>;

  updateReportConditionalSection(
    assessmentUid: string,
    sectionUid: string,
    request: UpdateReportConditionalSectionRequest,
    languageCode?: string,
  ): ObservableResult<UpdateReportSectionResponse>;

  updateReportResourceSection(
    assessmentUid: string,
    sectionUid: string,
    request: UpdateReportResourceSectionRequest,
    languageCode?: string,
  ): ObservableResult<UpdateReportSectionResponse>;

  updateReportFeedbackQuestionSection(
    assessmentUid: string,
    sectionUid: string,
    request: UpdateReportFeedbackQuestionRequest,
    languageCode?: string,
  ): ObservableResult<UpdateReportSectionResponse>;

  updateReportFeedbackStatsSection(
    assessmentUid: string,
    sectionUid: string,
    request: UpdateReportFeedbackStatsRequest,
    languageCode?: string,
  ): ObservableResult<UpdateReportSectionResponse>;

  updateReportFeedbackProvidersSection(
    assessmentUid: string,
    sectionUid: string,
    request: UpdateReportFeedbackProvidersRequest,
    languageCode?: string,
  ): ObservableResult<UpdateReportSectionResponse>;

  reorderReportSections(assessmentUid: string, request: ReportReordering): ObservableResult<Report>;

  deleteReportSection(assessmentUid: string, sectionUid: string): ObservableResult<void>;

  getReport(
    assessmentUid: string,
    responsesUid: string,
    fps?: string,
    languageCode?: string,
    targetUserUid?: string,
  ): ObservableResult<GeneratedReport>;

  showReportTitle(assessmentUid: string): ObservableResult<void>;

  hideReportTitle(assessmentUid: string): ObservableResult<void>;

  showHideReportOrgLogo(assessmentUid: string, request: ShowReportLogoRequest): ObservableResult<void>;

  updateReportMode(assessmentUid: string, mode: ReportMode): ObservableResult<void>;

  addReportResourceTag(assessmentUid: string, sectionUid: string, tagUid: string): ObservableResult<void>;

  getAssessmentsResponses(): ObservableResult<MyPagesAssessmentsResponse[]>;

  getSpecificRecipientFeedbacksSummary(recipientUid: string): ObservableResult<FeedbackRecipientSummaryEntry>;

  deleteReportResourceTag(assessmentUid: string, sectionUid: string, tagUid: string): ObservableResult<void>;

  getTextAnalysisStatus(assessmentUid: string): Observable<AssessmentTextAnalysisStatus>;

  resyncTextAnalysis(assessmentUid: string): Observable<void>;
}
